import { useCallback, useEffect, useRef, useState } from "react";
import { SaveBar, SaveError, SaveStatus } from "../../components/save";
import { useSaveState } from "../../lib/saving";
import { Button, Confirm, ErrorNote, Panel, Select } from "../../components/ui";
import { DueDateField, TextField } from "../../components/fields";
import { MoneyField } from "../../components/ui";
import { formatMoney, parseMoney, centsToInput } from "../../lib/money";
import {
  openPromiseFor, promiseHistory, makePromise, cancelPromise, today,
  STATUS_LABEL, type OpenPromise, type PromiseRow,
} from "./promises";

/**
 * When is the rest of the money coming.
 *
 * Shown on any deal that has taken money and is still owed some. That is the
 * state nobody tracks and everybody forgets: the customer paid five thousand,
 * said "Friday", drove off, and Friday exists only in somebody's memory.
 *
 * Saving is an explicit button with a confirmation that it worked, and a
 * message that says what went wrong if it did not — including the offline
 * case, which on a lot with patchy signal is the one that actually happens.
 * Nothing here saves on blur.
 */

const REMIND_CHOICES: { value: string; label: string }[] = [
  { value: "none", label: "No reminder" },
  { value: "0",    label: "On the day" },
  { value: "1",    label: "1 day before" },
  { value: "2",    label: "2 days before" },
  { value: "3",    label: "3 days before" },
  { value: "7",    label: "A week before" },
];

const niceDate = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleDateString("en-US",
        { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

const whenSent = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-US",
    { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

export default function BalanceDue({ dealId, owingCents, customerEmail, onChanged }: {
  dealId: string;
  owingCents: number;
  customerEmail: string | null;
  /** So the deal screen can refresh its own figures after a change here. */
  onChanged?: () => void;
}) {
  const [promise, setPromise] = useState<OpenPromise | null>(null);
  const [history, setHistory] = useState<PromiseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [remind, setRemind] = useState("2");

  const [saved, setSaved] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [why, setWhy] = useState("");

  /* The shared save pieces (see CLAUDE.md, "Saving data"): Unsaved changes,
     Saving…, Saved ✓ with the time once the database has answered, or Not
     saved with the reason — and a leave-page warning while a date is typed
     but not saved. */
  const opened = useRef("");
  const dirty = adding && JSON.stringify({ date, amount, note, remind }) !== opened.current;
  const dateSave = useSaveState({ dirty, what: "the balance due date" });
  const stopSave = useSaveState();
  const busy = dateSave.saving || stopSave.saving;

  const load = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([openPromiseFor(dealId), promiseHistory(dealId)]);
      setPromise(p);
      setHistory(h);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(e instanceof Error ? e.message : "Could not load the balance details.");
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { void load(); }, [load]);

  function openForm(from?: OpenPromise | null) {
    setSaved(null);
    dateSave.reset(); stopSave.dismiss();
    const start = {
      date: from?.promised_for ?? "",
      amount: from?.promised_cents ? centsToInput(from.promised_cents) : "",
      note: from?.note ?? "",
      remind: from ? (from.remind_customer ? String(from.remind_days_before) : "none") : "2",
    };
    opened.current = JSON.stringify(start);
    setDate(start.date); setAmount(start.amount); setNote(start.note); setRemind(start.remind);
    setAdding(true);
  }

  function cancelForm() {
    if (dirty && !window.confirm("Discard the date you entered?")) return;
    setAdding(false); dateSave.dismiss();
  }

  async function save() {
    setSaved(null);
    if (!date) { dateSave.fail("Pick the date the balance is coming."); return; }
    const cents = amount.trim() ? parseMoney(amount) : null;
    if (amount.trim() && (cents === null || cents <= 0)) {
      dateSave.fail("That amount does not look right. Leave it blank for the whole balance.");
      return;
    }
    const ok = await dateSave.run(() => makePromise({
        dealId,
        promisedFor: date,
        amountCents: cents,
        note: note || null,
        remindCustomer: remind !== "none",
        remindDaysBefore: remind === "none" ? 2 : Number(remind),
      }), { toast: "Balance due date saved" });
    if (!ok) return;            // everything typed stays in the form
    /* Committed. What follows is a refresh, not the save. */
    setAdding(false);
    setSaved(
      `${formatMoney(cents ?? owingCents)} due ${niceDate(date)}`
      + (remind === "none"
          ? ", no reminder will be sent."
          : customerEmail
            ? `, and ${customerEmail} will be emailed a reminder.`
            : ". No email on file for this customer, so no reminder can be sent."));
    await load();
    onChanged?.();
  }

  async function stopChasing(id: string) {
    setSaved(null);
    const ok = await stopSave.run(() => cancelPromise(id, why), { toast: "Stopped chasing" });
    // On a failure the dialog stays open with the reason still typed.
    if (!ok) return;
    setConfirming(false);
    setWhy("");
    setSaved("It is off the balances-due list.");
    try { await load(); onChanged?.(); } catch { /* shown on reload */ }
  }

  if (loading) return null;
  if (loadErr) return <ErrorNote>{loadErr}</ErrorNote>;

  const past = history.filter((h) => h.status !== "open");

  return (
    <Panel
      title="Balance due"
      rail={promise?.overdue ? "danger" : "work"}
      figure={formatMoney(owingCents)}
    >
      {!adding && (dateSave.status === "saved" || stopSave.status === "saved") && (
        <p className="savenote">
          <SaveStatus save={stopSave.savedAt
              && (!dateSave.savedAt || stopSave.savedAt > dateSave.savedAt) ? stopSave : dateSave}
            savedLabel="Saved" />
        </p>
      )}
      {saved && <p className="decode-ok">{saved}</p>}

      {!promise && !adding && (
        <>
          <p className="hint">
            {formatMoney(owingCents)} is still owing and no date has been
            recorded. Ask the customer when they are bringing it and put the
            date here — this is the only place it gets written down.
          </p>
          <div className="actions">
            <Button onClick={() => openForm(null)}>Record the date</Button>
          </div>
        </>
      )}

      {promise && !adding && (
        <>
          <dl className="spec">
            <div>
              <dt>Coming on</dt>
              <dd className={promise.overdue ? "warn" : ""}>
                {niceDate(promise.promised_for)}
                {promise.overdue
                  ? ` — ${Math.abs(promise.days_away)} day${Math.abs(promise.days_away) === 1 ? "" : "s"} late`
                  : promise.days_away === 0
                    ? " — today"
                    : ` — in ${promise.days_away} day${promise.days_away === 1 ? "" : "s"}`}
              </dd>
            </div>
            <div>
              <dt>Amount</dt>
              <dd>
                {promise.promised_cents
                  ? formatMoney(promise.promised_cents)
                  : `${formatMoney(promise.owing_cents)} — the whole balance`}
              </dd>
            </div>
            {promise.note && (
              <div><dt>Note</dt><dd>{promise.note}</dd></div>)}
            <div>
              <dt>Customer reminder</dt>
              <dd>
                {!promise.remind_customer
                  ? "Off"
                  : promise.reminder_error
                    ? `Failed — ${promise.reminder_error}`
                    : promise.reminded_at
                      ? `Sent ${whenSent(promise.reminded_at)}`
                      : !promise.customer_email
                        ? "No email on file — nothing will be sent"
                        : `${promise.remind_days_before === 0
                             ? "On the day" : `${promise.remind_days_before} days before`}`
                            + `, to ${promise.customer_email}`}
              </dd>
            </div>
          </dl>

          {confirming ? (
            <Confirm
              message={`Stop chasing the ${formatMoney(promise.chase_cents)} due `
                     + `${niceDate(promise.promised_for)}? The money is still owed — `
                     + "this only takes it off the list."}
              confirmLabel={stopSave.saving ? "Saving…" : "Stop chasing"} cancelLabel="Keep it"
              onCancel={() => { setConfirming(false); setWhy(""); stopSave.dismiss(); }}
              onConfirm={() => { if (!stopSave.saving) void stopChasing(promise.id); }}>
              <TextField label="Why" value={why} onChange={setWhy}
                         hint="Kept with the deal." />
              <SaveError save={stopSave}
                         hint="It is still on the list. The reason is still here — try again." />
            </Confirm>
          ) : (
            <div className="actions">
              <Button variant="ghost" onClick={() => openForm(promise)} disabled={busy}>
                They gave a new date
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(true)} disabled={busy}>
                Stop chasing
              </Button>
            </div>
          )}
        </>
      )}

      {adding && (
        <>
          <DueDateField label="Coming on" value={date} onChange={setDate} required
                        min={today()}
                        hint="The date the customer said they would bring it." />
          <MoneyField label="Amount" value={amount} onChange={setAmount}
                      hint={`Leave blank for the whole balance (${formatMoney(owingCents)}).`} />
          <TextField label="Note" value={note} onChange={setNote}
                     hint="Optional. “After payday”, “when the insurance cheque clears”." />
          <Select label="Remind the customer" value={remind} onChange={setRemind}
                  hint={customerEmail
                    ? `Emailed automatically to ${customerEmail}.`
                    : "This customer has no email on file, so nothing can be sent. "
                      + "Add one on the customer first."}>
            {REMIND_CHOICES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </Select>

          <SaveBar save={dateSave} onSave={() => void save()}
                   label={promise ? "Save the new date" : "Save the date"}
                   alwaysPrimary idle="Not saved yet"
                   retryHint="The date is still on screen — press Save to try again. Any earlier date is still on the deal.">
            <Button variant="ghost" disabled={busy} onClick={cancelForm}>Cancel</Button>
          </SaveBar>
          {promise && (
            <p className="hint dim">
              The date of {niceDate(promise.promised_for)} stays on the deal as a
              moved date, so you can see how many times this has shifted.
            </p>
          )}
        </>
      )}

      {past.length > 0 && (
        <>
          <h4 className="subhead">Earlier dates</h4>
          <ul className="timeline">
            {past.map((h) => (
              <li key={h.id}>
                <span className="dim">{niceDate(h.promised_for)}</span>
                {" — "}{STATUS_LABEL[h.status]}
                {h.amount_cents ? ` · ${formatMoney(h.amount_cents)}` : ""}
                {h.note ? ` · ${h.note}` : ""}
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
}
